import React, { FC, useEffect, useState } from "react";
import styles from "./Admin.module.scss";
import "../old/Payment/Payment.css";
import apiInstance from "../services/httpService";
import Switch from "@mui/material/Switch";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

const MONTHS: Record<string, string> = {
  "01": "JAN", "02": "FEB", "03": "MAR", "04": "APR", "05": "MAY", "06": "JUN",
  "07": "JUL", "08": "AUG", "09": "SEP", "10": "OCT", "11": "NOV", "12": "DEC",
};

/** A practice counts as "on" while it is scheduled or in progress. */
const ACTIVE_STATUSES = ["scheduled", "in_progress"];

/** Human-readable badge label for a practice status. */
const statusLabel = (status: string | null): string => {
  if (status === "scheduled") return "SCHEDULED";
  if (status === "in_progress") return "IN PROGRESS";
  return "NONE";
};

interface PracticeEvent {
  event_id: number;
  name: string;
  display_name: string;
  description?: string;
  address?: string;
  event_thumbnail?: string;
  date_details: { date: string; dummy_date: boolean };
}

interface ManagePracticeProps {
  /** Navigate to the Mark Attendance subtile (for the currently active practice). */
  onOpenMarkAttendance?: () => void;
}

const ManagePractice: FC<ManagePracticeProps> = ({ onOpenMarkAttendance }) => {
  const [events, setEvents] = useState<PracticeEvent[]>([]);
  const [activeEventId, setActiveEventId] = useState<number | null>(null);
  const [activeStatus, setActiveStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [confirm, setConfirm] = useState<{
    open: boolean;
    action: "open" | "close" | "cancel";
    event: PracticeEvent | null;
  }>({ open: false, action: "open", event: null });

  const token = localStorage.getItem("token");

  const Alert = React.forwardRef(function Alert(props: any, ref: any) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const toast = (message: string, severity: "success" | "error") =>
    setSnackbar({ open: true, message, severity });

  const loadEvents = () =>
    apiInstance
      .axiosCall("api/ccc/events/non-subscription", null, "GET")
      .then((res: any) => {
        if (res?.data?.status === "success") setEvents(res.data.data || []);
      })
      .catch(() => {
        toast("Could not load practices. Please try again.", "error");
      });

  // Only one practice can be active at a time. The toggle for an event is ON
  // when that event has a practice whose status is scheduled or in_progress.
  const loadActive = () =>
    apiInstance
      .axiosCall("api/ccc/practice/active", { token }, "POST")
      .then((res: any) => {
        const d = res?.data?.data;
        if (res?.data?.status === "success" && d && ACTIVE_STATUSES.includes(d.practice_status)) {
          setActiveEventId(d.event_id);
          setActiveStatus(d.practice_status);
        } else {
          setActiveEventId(null);
          setActiveStatus(null);
        }
      })
      .catch(() => {
        // 400 "No active practice" (or any error) => nothing active
        setActiveEventId(null);
        setActiveStatus(null);
      });

  useEffect(() => {
    // Load the events and the currently active practice together, so an event
    // that already has an active practice shows its toggle ON as soon as the
    // list renders (no flash of all-off toggles).
    Promise.all([loadEvents(), loadActive()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestToggle = (event: PracticeEvent, currentlyOn: boolean) => {
    if (!currentlyOn) {
      setConfirm({ open: true, action: "open", event });
    } else if (activeStatus === "in_progress") {
      // Practice has attendance marked — close it (preserve records).
      setConfirm({ open: true, action: "close", event });
    } else {
      // Practice was only scheduled, nothing marked yet — cancel/delete it.
      setConfirm({ open: true, action: "cancel", event });
    }
  };

  const closeConfirm = () => setConfirm((c) => ({ ...c, open: false }));

  const handleConfirmProceed = () => {
    const ev = confirm.event;
    const action = confirm.action;
    closeConfirm();
    if (!ev) return;

    setBusy(true);
    if (action === "open") {
      apiInstance
        .axiosCall("api/ccc/practice/start", { token, event_id: ev.event_id }, "POST")
        .then((res: any) => {
          setBusy(false);
          if (res?.data?.status === "success") {
            // Scheduled successfully → reflect ON immediately.
            setActiveEventId(ev.event_id);
            setActiveStatus("scheduled");
            toast(res.data.message || "Practice opened for today.", "success");
          } else {
            toast(res?.data?.message || "Could not open practice.", "error");
          }
        })
        .catch((err: any) => {
          setBusy(false);
          toast(err?.response?.data?.message || "Could not open practice.", "error");
        });
    } else if (action === "close") {
      // Practice is in_progress — close it and preserve attendance records.
      apiInstance
        .axiosCall("api/ccc/practice/close", { token }, "POST")
        .then((res: any) => {
          setBusy(false);
          if (res?.data?.status === "success") {
            setActiveEventId(null);
            setActiveStatus(null);
            toast(res.data.message || "Practice closed successfully.", "success");
          } else {
            toast(res?.data?.message || "Could not close the practice.", "error");
          }
        })
        .catch((err: any) => {
          setBusy(false);
          toast(err?.response?.data?.message || "Could not close the practice.", "error");
        });
    } else {
      // Practice is still scheduled (no attendance marked) — cancel/delete it.
      apiInstance
        .axiosCall("api/ccc/practice/delete", { token }, "DELETE")
        .then((res: any) => {
          setBusy(false);
          if (res?.data?.status === "success") {
            setActiveEventId(null);
            setActiveStatus(null);
            toast(res.data.message || "Practice cancelled.", "success");
          } else {
            toast(res?.data?.message || "Could not cancel the practice.", "error");
          }
        })
        .catch((err: any) => {
          setBusy(false);
          toast(err?.response?.data?.message || "Could not cancel the practice.", "error");
        });
    }
  };

  if (loading) {
    return (
      <div className={styles.Admin__loading}>
        <CircularProgress />
      </div>
    );
  }

  // Practices are opened for "today"; warn when today is not a Friday.
  const today = new Date();
  const isFridayToday = today.getDay() === 5;
  const todayName = today.toLocaleDateString(undefined, { weekday: "long" });

  return (
    <div className={styles.Admin__practice}>
      <div className="row event-item-row m-0">
        {events.length === 0 && (
          <div className={styles.Admin__practice_empty}>No active practices found.</div>
        )}

        {events.map((event) => {
          const isOn = activeEventId === event.event_id;
          const parts = (event.date_details?.date || "//").split("/");
          return (
            <div
              className="col-lg-4 my-3 event-item-wrapper"
              key={"practice-" + event.event_id}
              onClick={isOn ? () => onOpenMarkAttendance?.() : undefined}
              style={isOn ? { cursor: "pointer" } : undefined}
              title={isOn ? "Open Mark Attendance for this practice" : undefined}
            >
              <div className="event-item">
                <img
                  className="event-img"
                  alt=""
                  src={
                    event.event_thumbnail
                      ? event.event_thumbnail
                      : require("../../assets/Images/Ladies.jpg")
                  }
                />
                <div className="row event-info p-3">
                  <div className="col-lg-3 col-3 text-center mb-3">
                    <h3 className="m-0 mt-1 event-month">{MONTHS[parts[1]]}</h3>
                    {event.date_details?.dummy_date === false && (
                      <h3 className="m-0 event-date">{parts[0]}</h3>
                    )}
                    <h3 className="m-0 mt-1 event-year">{parts[2]}</h3>
                  </div>
                  <div className="col-lg-9 col-9">
                    <h5 className="event-title" title={event.name}>
                      {event.display_name}
                    </h5>
                    <div
                      className={styles.Admin__practice_toggle}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Switch
                        size="small"
                        checked={isOn}
                        disabled={busy}
                        onChange={() => requestToggle(event, isOn)}
                      />
                      <span
                        className={`${styles.Admin__practice_badge} ${
                          isOn
                            ? styles.Admin__practice_badge_open
                            : styles.Admin__practice_badge_none
                        }`}
                      >
                        {isOn ? statusLabel(activeStatus) : "NONE"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirmation dialog */}
      <Dialog open={confirm.open} onClose={closeConfirm}>
        <DialogTitle>
          {confirm.action === "open"
            ? "Open Practice"
            : confirm.action === "close"
            ? "Close Practice"
            : "Cancel Practice"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm.action === "open"
              ? `Do you really want to open a practice today for "${confirm.event?.display_name}"?`
              : confirm.action === "close"
              ? `Do you want to close today's practice for "${confirm.event?.display_name}"? Attendance records will be preserved.`
              : `Do you want to cancel today's practice for "${confirm.event?.display_name}"? No attendance has been marked yet.`}
          </DialogContentText>
          {confirm.action === "open" && !isFridayToday && (
            <DialogContentText sx={{ mt: 1.5, color: "#c62828", fontWeight: 600 }}>
              Note: Today is {todayName}, not a Friday. Are you sure you intend to start the
              practice today?
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} color="secondary">
            Dismiss
          </Button>
          <Button
            onClick={handleConfirmProceed}
            variant="contained"
            sx={{ backgroundColor: "#3a8bac", "&:hover": { backgroundColor: "#195d7a" } }}
          >
            {confirm.action === "open"
              ? "Open Practice"
              : confirm.action === "close"
              ? "Close Practice"
              : "Cancel Practice"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ManagePractice;
